/**
 * BibTeX / biblatex entries for the records `/api/cite` resolves.
 *
 * Each builder returns a draft — entry type, a base key, and its fields — rather than
 * text, because keys are only final once the whole batch is known: two Smith 2024
 * papers become smith2024a and smith2024b. `renderBibDrafts` makes the keys unique and
 * writes the entries.
 *
 * The records arrive flattened: authors as one natural-order string ("Tai-Bao Gao,
 * Zhong-Ze Qu"), dates as ISO text ("2017-11-14"). The helpers here read them back.
 */

import type { PersonName } from "@/lib/tool-kit/citation-parts";
import {
  type BibField,
  type BibTextOptions,
  bibText,
  bibtexName,
  citationKey,
  formatBibEntry,
  joinNames,
  monthMacro,
  personFromNatural,
  plainText,
  uniqueKeys,
} from "@/lib/tool-kit/identifiers/bibtex";
import type { PubmedId } from "@/lib/tool-kit/identifiers/identifier-input";
import type { CiteEntry } from "@/lib/tool-kit/identifiers/lookup";

export interface BibDraft {
  type: string;
  key: string;
  fields: BibField[];
}

/* ---------------------------------------------------------------- reading */

const RE_ISSUED = /^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?/;
const RE_AUTHOR_SPLIT = /\s*,\s*/;
const RE_PAGE_RANGE = /^(\d+)\s*[-\u2010-\u2015\u2212]+\s*(\d+)$/;
const RE_ANY_DASH = /\s*[-\u2010-\u2015\u2212]+\s*/;
const RE_KEEP_FINAL_PERIOD = /(?:\.\p{L}\.|\.\.)$/u;
const RE_NLM_QUALIFIER = /\s*\([^()]*\)\s*$/;
const RE_WWW = /^www\d*\./;

export interface EntryDate {
  year: string;
  month?: number;
  day?: number;
}

/** "2017-11-14" → { year: "2017", month: 11, day: 14 }. */
export function entryDate(issued: string | undefined): EntryDate {
  const match = RE_ISSUED.exec(issued ?? "");
  if (!match) {
    return { year: "" };
  }
  return {
    year: match[1],
    month: match[2] ? Number(match[2]) : undefined,
    day: match[3] ? Number(match[3]) : undefined,
  };
}

/** ISO date text for biblatex's `date` field: 2017, 2017-11 or 2017-11-14. */
export function isoDate(date: EntryDate): string {
  if (!date.year) {
    return "";
  }
  const parts = [date.year];
  if (date.month) {
    parts.push(String(date.month).padStart(2, "0"));
    if (date.day) {
      parts.push(String(date.day).padStart(2, "0"));
    }
  }
  return parts.join("-");
}

/** "Tai-Bao Gao, Zhong-Ze Qu" → two people; a group author is kept whole. */
export function entryPeople(names: string | undefined): PersonName[] {
  return (names ?? "")
    .split(RE_AUTHOR_SPLIT)
    .map(personFromNatural)
    .filter((person): person is PersonName => person !== null);
}

/**
 * MEDLINE abbreviates page ranges — "1123-9" means 1123–1129 — so an end page shorter
 * and smaller than the start borrows the start's leading digits.
 */
export function expandPageRange(page: string): string {
  const match = RE_PAGE_RANGE.exec(page.trim());
  if (!match) {
    return page.trim();
  }
  const [, start, end] = match;
  if (end.length < start.length) {
    const full = `${start.slice(0, start.length - end.length)}${end}`;
    if (Number(full) > Number(start)) {
      return `${start}-${full}`;
    }
  }
  return `${start}-${end}`;
}

/** BibTeX writes a page range with an en dash: 15443--15450. */
export function bibPages(page: string | undefined): string {
  const trimmed = page?.trim() ?? "";
  if (trimmed === "") {
    return "";
  }
  return expandPageRange(trimmed).split(RE_ANY_DASH).join("--");
}

/**
 * MEDLINE ends every article title with a period; a bibliography style adds its own.
 * An initialism's period ("U.S.") and an ellipsis are left alone.
 */
export function withoutTrailingPeriod(title: string): string {
  const trimmed = title.trim();
  if (!trimmed.endsWith(".") || RE_KEEP_FINAL_PERIOD.test(trimmed)) {
    return trimmed;
  }
  return trimmed.slice(0, -1);
}

/** "Dalton transactions (Cambridge, England : 2003)" → "Dalton transactions". */
export function withoutNlmQualifier(journal: string): string {
  return journal.replace(RE_NLM_QUALIFIER, "").trim();
}

function authorList(people: PersonName[], options: BibTextOptions): string {
  return joinNames(people.map((person) => bibtexName(person, options)));
}

/* ---------------------------------------------------------------- builders */

export type PubmedIdPlacement = "field" | "note" | "eprint";

export interface PubmedDraftOptions extends BibTextOptions {
  /** The journal name to write: an NLM abbreviation, or the title. */
  journal: string;
  placement: PubmedIdPlacement;
}

function pubmedIdFields(
  id: PubmedId,
  placement: PubmedIdPlacement
): BibField[] {
  const label = id.kind === "pmid" ? "PMID" : "PMCID";
  if (placement === "note") {
    return [{ name: "note", value: `${label}: ${id.id}` }];
  }
  // biblatex knows `pubmed` as an eprint type; it has none for PMC ids.
  if (placement === "eprint" && id.kind === "pmid") {
    return [
      { name: "eprint", value: id.id },
      { name: "eprinttype", value: "pubmed" },
    ];
  }
  return [{ name: id.kind, value: id.id }];
}

/** @article from a PubMed record. */
export function pubmedArticleDraft(
  entry: CiteEntry,
  id: PubmedId,
  options: PubmedDraftOptions
): BibDraft {
  const authors = entryPeople(entry.author);
  const date = entryDate(entry.issued);
  const title = withoutTrailingPeriod(plainText(entry.title ?? ""));
  return {
    type: "article",
    key: citationKey({ author: authors[0], year: date.year, title }),
    fields: [
      { name: "author", value: authorList(authors, options) },
      { name: "title", value: bibText(title, options, { isTitle: true }) },
      { name: "journal", value: bibText(options.journal, options) },
      { name: "year", value: date.year },
      { name: "month", value: monthMacro(date.month), bare: true },
      { name: "volume", value: entry.volume ?? "" },
      { name: "number", value: entry.issue ?? "" },
      { name: "pages", value: bibPages(entry.page) },
      { name: "doi", value: entry.DOI ?? "" },
      ...pubmedIdFields(id, options.placement),
    ],
  };
}

const RE_SHORT_SECOND_LEVEL = /^(?:ac|co|com|edu|gov|net|org)$/;

/**
 * The site's own name from its address, for a page with no author and no site name:
 * who.int → "who", en.wikipedia.org → "wikipedia", www.bbc.co.uk → "bbc".
 */
export function siteFromHost(url: string): string {
  let host = "";
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
  const labels = host.replace(RE_WWW, "").split(".");
  if (labels.length < 2) {
    return labels[0] ?? "";
  }
  const secondLevel = labels.at(-2) ?? "";
  const countryPair =
    labels.length >= 3 &&
    (labels.at(-1) ?? "").length === 2 &&
    RE_SHORT_SECOND_LEVEL.test(secondLevel);
  return countryPair ? (labels.at(-3) ?? "") : secondLevel;
}

export interface WebpageDraftOptions extends BibTextOptions {
  format: "bibtex" | "biblatex";
  /** ISO date the page was read: 2026-10-05. */
  accessed: string;
}

/** @misc with howpublished = {\url{…}} and an "Accessed" note, or biblatex @online. */
export function webpageDraft(
  entry: CiteEntry,
  url: string,
  options: WebpageDraftOptions
): BibDraft {
  const authors = entryPeople(entry.author);
  const date = entryDate(entry.issued);
  const title = entry.title ?? "";
  const site = entry.containerTitle ?? entry.publisher ?? "";
  const key = citationKey({
    author: authors[0],
    fallback: site || siteFromHost(url),
    year: date.year,
    title,
  });
  const common: BibField[] = [
    { name: "author", value: authorList(authors, options) },
    { name: "title", value: bibText(title, options, { isTitle: true }) },
  ];
  if (options.format === "biblatex") {
    return {
      type: "online",
      key,
      fields: [
        ...common,
        { name: "organization", value: bibText(site, options) },
        { name: "date", value: isoDate(date) },
        { name: "url", value: url },
        { name: "urldate", value: options.accessed },
      ],
    };
  }
  return {
    type: "misc",
    key,
    fields: [
      ...common,
      { name: "howpublished", value: `\\url{${url}}` },
      { name: "organization", value: bibText(site, options) },
      { name: "year", value: date.year },
      { name: "month", value: monthMacro(date.month), bare: true },
      {
        name: "note",
        value: options.accessed ? `Accessed: ${options.accessed}` : "",
      },
    ],
  };
}

/** Unique keys across the batch, then the entries, blank-line separated. */
export function renderBibDrafts(drafts: readonly BibDraft[]): string {
  const keys = uniqueKeys(drafts.map((draft) => draft.key));
  const text = drafts
    .map((draft, index) =>
      formatBibEntry(draft.type, keys[index], draft.fields)
    )
    .join("\n\n");
  return text === "" ? "" : `${text}\n`;
}
